import React, { useState, useEffect, useCallback } from "react";
import { useFormik } from "formik";
import axiosInstance from "../../../../../Services/axiosCommon";
import { useToast } from "../../../../../Components/Others/ToastServices";
import { NuInputSetting } from "../../../../../Components/Utils/Input";
import { TextMediumXS } from "../../../../../Components/Text/MediumText";
import ModalContainer from "../../../../../Components/Others/ModalContainer";
import EditIcon from "../../../../../Components/Icons/Edit";
import capitalizeFirstLetter from "../../../../../Components/Utils/Funcs";

const ChecklistStatusManager = ({
  status = "verify",
  viewPropertyName,
  onDataChange,
}) => {
  const toast = useToast();
  const [data, setData] = useState([]);
  const [isStatusActive, setIsStatusActive] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editLoading, setEditLoading] = useState(false);

  const displayName =
    viewPropertyName || `${capitalizeFirstLetter(status)} Checklist`;

  // Fetch checklist items for this status without firing error toast on mount
  const fetchChecklist = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/po-checklist/?status=${status}`);
      let list = res?.data?.data || res?.data || [];
      if (!Array.isArray(list)) {
        list = [];
      }
      // Filter by status in case backend returns all items
      const filtered = list.filter(
        (item) =>
          !item?.status ||
          item?.status?.toLowerCase() === status.toLowerCase()
      );
      setData(filtered);

      // Determine active state from list or response
      if (filtered.length > 0) {
        const anyInactive = filtered.some(
          (i) => i.isActive === false || i.is_active === false
        );
        setIsStatusActive(!anyInactive);
      } else {
        const resIsActive = res?.data?.isActive ?? res?.data?.is_active;
        if (typeof resIsActive === "boolean") {
          setIsStatusActive(resIsActive);
        } else {
          setIsStatusActive(false);
        }
      }

      onDataChange?.(filtered);
    } catch (err) {
      console.warn(`Could not fetch ${status} checklist:`, err);
      setData([]);
      setIsStatusActive(false);
    }
  }, [status, onDataChange]);

  useEffect(() => {
    fetchChecklist();
  }, [fetchChecklist]);

  // Bulk update isActive state for all checklists matching this status
  const handleToggleStatus = async () => {
    const nextState = !isStatusActive;
    setToggleLoading(true);
    try {
      const res = await axiosInstance.put(`/po-checklist/toggle-status`, {
        status: status.toLowerCase(),
        isActive: nextState,
      });

      if (res?.data?.status !== false) {
        setIsStatusActive(nextState);
        fetchChecklist();
        toast.open({
          type: "success",
          message: `${displayName} set to ${nextState ? "Active" : "Inactive"}`,
        });
      } else {
        toast.open({
          type: "error",
          message: res?.data?.message || "Failed to update active state",
        });
      }
    } catch (err) {
      console.error("Error toggling checklist status:", err);
      toast.open({
        type: "error",
        message:
          err?.response?.data?.message || "Failed to update active state",
      });
    } finally {
      setToggleLoading(false);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      value: "",
    },
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values, { resetForm }) => {
      const trimmed = values?.value?.trim();
      if (!trimmed) {
        toast.open({
          type: "error",
          message: "Please enter a checklist item",
        });
        return;
      }

      try {
        const res = await axiosInstance.post(`/po-checklist/`, {
          status: status.toLowerCase(),
          value: trimmed,
        });

        if (res?.data?.status !== false) {
          resetForm();
          fetchChecklist();
          toast.open({
            type: "success",
            message: `${displayName} added successfully`,
          });
        } else {
          toast.open({
            type: "error",
            message: res?.data?.message || `Failed to add ${displayName}`,
          });
        }
      } catch (err) {
        console.error("Error creating checklist item:", err);
        toast.open({
          type: "error",
          message:
            err?.response?.data?.message || `Failed to add ${displayName}`,
        });
      }
    },
  });

  const handleDelete = (id, itemText) => {
    if (!id) return;
    axiosInstance
      .delete(`/po-checklist/${id}`)
      .then((res) => {
        if (res?.data?.status !== false) {
          fetchChecklist();
          toast.open({
            type: "success",
            message: `${displayName} deleted successfully`,
          });
        } else {
          toast.open({
            type: "error",
            message: res?.data?.message || `Failed to delete item`,
          });
        }
      })
      .catch((err) => {
        console.error("Error deleting checklist item:", err);
        toast.open({
          type: "error",
          message: err?.response?.data?.message || `Failed to delete item`,
        });
      });
  };

  const handleEditSubmit = async () => {
    if (!editingItem) return;
    const trimmed = editValue?.trim();
    if (!trimmed) {
      toast.open({
        type: "error",
        message: "Checklist text cannot be empty",
      });
      return;
    }

    const id = editingItem.id || editingItem._id;
    setEditLoading(true);
    try {
      const res = await axiosInstance.put(`/po-checklist/${id}`, {
        status: status.toLowerCase(),
        value: trimmed,
      });

      if (res?.data?.status !== false) {
        setEditingItem(null);
        setEditValue("");
        fetchChecklist();
        toast.open({
          type: "success",
          message: `${displayName} updated successfully`,
        });
      } else {
        toast.open({
          type: "error",
          message: res?.data?.message || "Failed to update item",
        });
      }
    } catch (err) {
      console.error("Error updating item:", err);
      toast.open({
        type: "error",
        message: err?.response?.data?.message || "Failed to update item",
      });
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <div className="w-full">
      <form onSubmit={formik.handleSubmit}>
        <div className="w-full text-xs">
          <div className="px-3 mb-1 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <label className="font-medium text-textColor-500">
                {displayName}
              </label>
              <span
                className={`text-[10px] font-medium px-1.5 py-0.2 rounded border ${isStatusActive
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-gray-100 text-gray-500 border-gray-200"
                  }`}
              >
                {isStatusActive ? "Active" : "Inactive"}
              </span>
            </div>

            {/* Toggle Switch */}
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={toggleLoading}
                onClick={handleToggleStatus}
                title={`Toggle ${displayName} ${isStatusActive ? "Inactive" : "Active"
                  }`}
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${toggleLoading ? "opacity-60 cursor-not-allowed" : ""
                  } ${isStatusActive ? "bg-emerald-500" : "bg-gray-300"}`}
              >
                <span
                  className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isStatusActive ? "translate-x-4" : "translate-x-0"
                    }`}
                />
              </button>
            </div>
          </div>

          <div className={data.length === 0 ? "hidden" : "block px-3 mb-1"}>
            <NuInputSetting
              type="text"
              placeholder={displayName}
              formik={formik}
              name="value"
            />
          </div>
        </div>
      </form>
      {
        data.length === 0 ? <></>
          :
          <div className="px-3">
            <div
              className={`w-full rounded-lg h-44 bg-[#f5f3ff] border border-gray-300 overflow-x-hidden overflow-y-auto transition-opacity ${!isStatusActive ? "opacity-70" : "opacity-100"
                }`}
            >
              <ul className="">
                {data.length === 0 ? (
                  <li className="m-1 flex justify-center items-center text-xs text-gray-400 py-6">
                    No checklist items
                  </li>
                ) : (
                  data.map((item, idx) => {
                    const itemText =
                      item?.value || item?.label || item?.name || "";
                    const itemId = item?.id || item?._id;
                    const itemActive =
                      item?.isActive !== false && item?.is_active !== false;

                    return (
                      <li
                        className={`m-1 flex justify-between items-center text-xs bg-white border border-gray-300 rounded text-main-title py-2 px-2 hover:bg-[#B3EBD7] hover:bg-opacity-50 ${!itemActive ? "opacity-60" : ""
                          }`}
                        key={itemId || idx}
                      >
                        <div className="flex items-center gap-1.5 flex-1 min-w-0 pr-2">
                          <TextMediumXS
                            color={itemActive ? "text-[#697565]" : "text-gray-400"}
                            content={itemText || "-"}
                          />
                          {!itemActive && (
                            <span className="text-[10px] text-amber-600 shrink-0">
                              (Inactive)
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span
                            onClick={() => {
                              setEditingItem(item);
                              setEditValue(itemText);
                            }}
                            className="text-baseColor-primary cursor-pointer hover:opacity-75"
                            title="Edit"
                          >
                            <EditIcon color="currentColor" width="12" height="12" />
                          </span>
                          <span
                            onClick={() => handleDelete(itemId, itemText)}
                            className="text-blue-900 cursor-pointer font-bold px-1"
                            title="Delete"
                          >
                            x
                          </span>
                        </div>
                      </li>
                    );
                  })
                )}
              </ul>
            </div>
          </div>
      }
      {/* Edit Modal */}
      <ModalContainer
        visiable={!!editingItem}
        title={`Edit ${displayName}`}
        okText="Save"
        content={
          <div className="p-2 min-w-[280px] sm:min-w-[360px]">
            <label className="text-xs font-semibold text-[#111537] mb-1.5 block">
              Checklist Requirement: <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="w-full text-xs p-2.5 border border-gray-300 rounded-md focus:outline-none focus:border-baseColor-primary"
              placeholder="Enter requirement text..."
            />
          </div>
        }
        handleClick={handleEditSubmit}
        closeModal={() => {
          setEditingItem(null);
          setEditValue("");
        }}
        isLoading={editLoading}
        disableOkButton={!editValue.trim()}
      />
    </div>
  );
};

export default ChecklistStatusManager;
